/**
 * The unified magazine list — CMS rows mapped to the `MagazineIssue` union
 * shape, with the static `MAGAZINES` array as fallback.
 *
 * Extracted from MagazinePage.tsx so the homepage MagazineFeature slider can
 * share the exact same source of truth as the /magazine archive. The CMS
 * source is preferred; the static array only kicks in when the generated
 * module is empty (e.g. fresh deploy before the first `npm run cms:sync`).
 */
import { MAGAZINES } from '@/content/magazines'
import type {
  ArticleLinkMagazineIssue,
  ExternalMagazineIssue,
  ImageMagazineIssue,
  MagazineIssue,
  PdfMagazineIssue,
} from '@/content/magazines'
import { cmsMagazines, type CmsMagazineIndexEntry } from '@/content/cms/magazines.generated'
import { cmsMediaIndex } from '@/content/cms/media.generated'

/**
 * Resolve a CMS media uuid to its best display URL plus the native pixel
 * dimensions needed for an orientation-aware aspect frame. Returns undefined
 * when the uuid is absent or unknown in the generated media index (e.g. fresh
 * deploy before the CMS sync has populated it).
 */
function resolveCmsCover(
  uuid: string | null | undefined
): { url: string; width: number | null; height: number | null } | undefined {
  if (!uuid) return undefined
  const entry = cmsMediaIndex[uuid]
  if (!entry) return undefined
  return {
    url: entry.urls.medium ?? entry.urls.thumb ?? entry.urls.original,
    width: entry.width,
    height: entry.height,
  }
}

/**
 * Resolve a CMS PDF media uuid to its original (untransformed) URL — PDFs do
 * not have thumb/medium variants, so we always use the original. Falls back to
 * the legacy `pdf_path` string when the uuid is absent or unknown, so
 * magazines hosted outside the media library continue to render.
 */
function resolveCmsPdfPath(
  uuid: string | null | undefined,
  legacyPath: string | null | undefined
): string | null {
  if (uuid) {
    const entry = cmsMediaIndex[uuid]
    if (entry) return entry.urls.original
  }
  return legacyPath ?? null
}

/**
 * Format the CMS publication date to the `YYYY-MM-DD` shape that the existing
 * `MagazineIssue.date` field expects. Prefers `issue_date` (the operator-set
 * publication/issue date) and falls back to `published_at` (the CMS row's
 * publish time) for legacy rows where `issue_date` was never populated — the
 * seeder for the static-magazine migration only stamped `published_at`, so
 * without this fallback ~2/3 of the archive rendered an empty calendar cell.
 */
function toIsoDate(cms: CmsMagazineIndexEntry): string {
  const ts = cms.issue_date ?? cms.published_at
  if (ts == null) return ''
  try {
    return new Date(ts).toISOString().slice(0, 10)
  } catch {
    return ''
  }
}

/**
 * Convert a CMS magazine index entry to the discriminated-union
 * `MagazineIssue` shape. Returns null for entries that lack the minimum
 * required fields (e.g. a `pdf` entry with no `pdf_path`, or an
 * `external`/`image` entry with no `external_url`).
 */
function cmsToMagazineIssue(
  cms: (typeof cmsMagazines)[number]
): MagazineIssue | null {
  const cover = resolveCmsCover(cms.cover_media_uuid)
  const base = {
   slug: cms.slug,
    // The media row behind the cover — consumers serve it from the
    // responsive ladder when it has one.
    coverMediaUuid: cms.cover_media_uuid ?? undefined,
    publication: cms.publication,
    publicationEn: cms.publication,
    issue: cms.issue,
    issueEn: cms.issue,
    date: toIsoDate(cms),
    interviewee: cms.interviewee,
    title: cms.title,
    summary: cms.summary,
    coverImage: cover?.url,
    coverWidth: cover?.width ?? undefined,
    coverHeight: cover?.height ?? undefined,
  }

  if (cms.kind === 'pdf') {
    const pdfPath = resolveCmsPdfPath(cms.pdf_media_uuid, cms.pdf_path)
    if (!pdfPath) return null
    return {
      ...base,
      kind: 'pdf',
      pdfPath,
      externalUrl: cms.external_url ?? undefined,
    } satisfies PdfMagazineIssue
  }

  if (cms.kind === 'external') {
    if (!cms.external_url) return null
    return {
      ...base,
      kind: 'external',
      externalUrl: cms.external_url,
    } satisfies ExternalMagazineIssue
  }

  if (cms.kind === 'image') {
    if (!cms.external_url) return null
    return {
      ...base,
      kind: 'image',
      coverImage: base.coverImage ?? '',
      coverWidth: base.coverWidth ?? 0,
      coverHeight: base.coverHeight ?? 0,
      externalUrl: cms.external_url,
    } satisfies ImageMagazineIssue
  }

  if (cms.kind === 'article-link') {
    if (!cms.external_url) return null
    return {
      ...base,
      kind: 'article-link',
      externalUrl: cms.external_url,
    } satisfies ArticleLinkMagazineIssue
  }

  return null
}

/**
 * The unified press-coverage list, newest first. Items without a resolvable
 * date sink to the end — the CMS ORDER BY only sees `issue_date`, so rows
 * with a null `issue_date` come back in arbitrary order and we own the final
 * sort here. JavaScript's sort is stable, so ties keep their original order.
 */
export function unifiedMagazines(): MagazineIssue[] {
  return (
    cmsMagazines.length > 0
      ? cmsMagazines
          .map(cmsToMagazineIssue)
          .filter((m): m is MagazineIssue => m !== null)
      : MAGAZINES
  ).sort((a, b) => {
    const ta = a.date ? Date.parse(a.date) : Number.NEGATIVE_INFINITY
    const tb = b.date ? Date.parse(b.date) : Number.NEGATIVE_INFINITY
    return tb - ta
  })
}
